import './OrbitConfigModal.scss'

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Cesium from 'cesium'
import {
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    Modal,
    ModalBody,
    ModalHeader
} from '../../../../../CommonComponents/modals/Modal'
import { CloseSvg, EditSvg, EpochInfoIcon, RAANPhasingButton, SaveSvg, TAPhasingButton } from '../../../../../CommonComponents/svgs/Svgs'

import useModal from '../../../hooks/useModal'
import useForm from '../../../hooks/useForm'
import useViewer from '../../../hooks/useViewer'
import useStaticData from '../../../hooks/useStaticData'
import DatetimeInput from '../../date-time-input/DatetimeInput'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { ORBIT_FIELDS } from '../../../../../../utils/constants'
import { onKeyDown, showToastMessage, validateInputRange, validateOrbitConfigForm } from '../../../../../../utils/commonFunctions'
import { FormGroupDetailsType } from '../../../utils/scenario-types'
import { get_formated_time } from '../../../../utils';
import {
    buildMultiGroupPreviewCzml,
    GroupPreviewParams,
    loadPreviewDataSource,
    removePreviewWithRef,
} from '../../../../../modelling/utils/orbitCalculations'
import dayjs from 'dayjs'

const CONFIG_PREVIEW_DS = '__scenario_orbit_preview__';

const OrbitConfigModal = () => {
    const { modals, setModals } = useModal()
    const { formData, setFormData } = useForm()
    const { viewer } = useViewer()
    const { allGroups } = useStaticData()

    const [autoPhasing, setAutoPhasing] = useState(true)

    const open = useMemo(() => modals?.configured?.name === 'orbitConfig', [modals?.configured?.name])
    const configData = useMemo(() => modals?.configured?.data as FormGroupDetailsType, [modals?.configured?.data])
    const orbitData = useMemo(() => configData?.referenceOrbit, [configData?.referenceOrbit])
    const scenarioStartTime = useMemo(() => formData.details.scenarioStartTime, [formData.details.scenarioStartTime])

    const onEdit = useMemo(() => modals?.configured?.onEdit || false, [modals?.configured?.onEdit])

    // ─── Live orbit preview ───
    const dsRef = useRef<Cesium.CzmlDataSource | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const groupTypeMap = useMemo(() => {
        const m: Record<string, (typeof allGroups)[0]> = {};
        for (const g of allGroups) m[g.groupId] = g;
        return m;
    }, [allGroups]);

    const removeConfigPreview = useCallback(() => {
        removePreviewWithRef(viewer, dsRef, CONFIG_PREVIEW_DS);
    }, [viewer]);

    const loadConfigPreview = useCallback((editedGroup: FormGroupDetailsType) => {
        if (!viewer || !editedGroup?.referenceOrbit) { removeConfigPreview(); return; }

        // Merge editedGroup with the rest of formData.satellites
        const mergedGroups: FormGroupDetailsType[] = (formData.satellites || []).map(sat =>
            sat.groupId === editedGroup.groupId ? editedGroup : sat
        );

        const groupParams: GroupPreviewParams[] = mergedGroups
            .filter(gp => gp.referenceOrbit)
            .map(gp => {
                const orbit = gp.referenceOrbit;
                const fullGroup = groupTypeMap[gp.groupId];
                const isPlanePhasing = gp.phasingType === 'PlanePhasing';
                const isMulti = isPlanePhasing
                    ? gp.satCount > 1
                    : (fullGroup?.groupType === 'Multi Plane' || gp.noOfPlanes > 1);
                return {
                    groupId: gp.groupId,
                    groupName: gp.groupName,
                    altitude: Number(orbit.altitude),
                    eccentricity: Number(orbit.eccentricity),
                    inclination: Number(orbit.inclination),
                    RAAN: Number(orbit.RAAN),
                    AP: Number(orbit.AP),
                    TA: Number(orbit.TA),
                    epoch: Number(orbit.epoch),
                    startTime: Number(scenarioStartTime),
                    satCount: gp.satCount,
                    noOfPlanes: isPlanePhasing ? gp.satCount : gp.noOfPlanes,
                    noOfSatellitesPerPlane: isPlanePhasing ? 1 : gp.noOfSatellitesPerPlane,
                    phasingAngle: isPlanePhasing ? 0 : gp.phasingAngle,
                    planeSeparationAngle: isPlanePhasing ? gp.phasingAngle : gp.separationAngleBetweenPlane,
                    isMultiPlane: isMulti,
                    color: gp.color,
                };
            });

        if (groupParams.length === 0) { removeConfigPreview(); return; }

        const czml = buildMultiGroupPreviewCzml(groupParams, CONFIG_PREVIEW_DS);
        removeConfigPreview();
        dsRef.current = loadPreviewDataSource(viewer, czml, CONFIG_PREVIEW_DS);
    }, [viewer, formData.satellites, removeConfigPreview, groupTypeMap, scenarioStartTime]);

    // Re-render orbit preview whenever configData changes (user edits a field)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!viewer || !open || !configData) { return; }
        debounceRef.current = setTimeout(() => loadConfigPreview(configData), 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [viewer, open, configData, loadConfigPreview]);

    // Guard: if the modal toggles closed we should also clear any preview
    // (component stays mounted because we `return null` when !open).
    useEffect(() => {
        if (!open) {
            removeConfigPreview();
            if (debounceRef.current) clearTimeout(debounceRef.current);
        }
    }, [open, removeConfigPreview]);

    // Cleanup preview on unmount
    useEffect(() => {
        return () => { removeConfigPreview(); if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [removeConfigPreview]);

    const setOnEdit = useCallback((onEdit: boolean) => {
        setModals((prev) => ({
            ...prev!,
            configured: {
                ...prev.configured!,
                onEdit
            }
        }))
    }, [])

    const closeModal = useCallback(() => {
        removeConfigPreview()
        setOnEdit(false)
        setModals((prev) => ({
            ...prev,
            configured: null
        }))
    }, [removeConfigPreview])

    const getEpochDetails = useCallback(() => {
        const epochTime = get_formated_time(orbitData?.epoch)
        const time = `${epochTime.split('T')[0]} ${epochTime.split('T')[1].split('Z')[0]} UTC`
        return time
    }, [orbitData?.epoch])

    const onAutoPhasing = () => {
        setAutoPhasing((prev) => {
            if (!prev) {
                setModals((prev) => ({
                    ...prev!,
                    configured: {
                        ...prev.configured!,
                        data: {
                            ...prev.configured?.data!,
                            phasingAngle: parseFloat((360 / prev.configured?.data?.satCount!).toFixed(2)) % 1 === 0
                                ? parseInt((360 / prev.configured?.data?.satCount!).toFixed(2))
                                : parseFloat((360 / prev.configured?.data?.satCount!).toFixed(2))
                        }
                    }
                }))
            }
            return !prev
        })
    }

    const validateEpochTime = (date: number) => {
        // check the date is valid or not
        const scenarioDuration = 1440 * 60
        if (!date) {
            showToastMessage('info', "Please enter valid scenario start time")
            return false
        } else if (dayjs.utc(date * 1000).year() < 2002 || dayjs.utc((date + scenarioDuration) * 1000).year() > 2037) {
            showToastMessage('info', "Select a start time between January 1, 2002 and December 30, 2037")
            return false
        } else {
            return true
        }
    }

    const onChangeEpoch = (key: string, value: number) => {
        const validate = validateEpochTime(value)
        if (!validate) return
        setModals((prev) => ({
            ...prev!,
            configured: {
                ...prev.configured!,
                data: {
                    ...prev.configured?.data!,
                    referenceOrbit: {
                        ...prev.configured?.data?.referenceOrbit!,
                        [key]: value as number
                    }
                } as FormGroupDetailsType
            }
        }))
    }

    // Common function to update orbit data
    const updateOrbitData = useCallback((key: string, value: any) => {
        setModals((prev) => ({
            ...prev!,
            configured: {
                ...prev.configured!,
                data: {
                    ...prev.configured?.data!,
                    phasingAngle: key === 'phasingAngle' ? value : prev.configured?.data?.phasingAngle!,
                    referenceOrbit: key === 'phasingAngle' ? prev.configured?.data?.referenceOrbit! : {
                        ...prev.configured?.data?.referenceOrbit!,
                        [key]: value
                    }
                } as FormGroupDetailsType
            }
        }))
    }, [modals?.configured?.data, setModals])


    const onChangeValue = (name: string, key: string, value: string | number | null, min: number, max: number, length: number, param: string) => {

        const validation = validateInputRange(name, value, min, max, length, param)
        if (validation.success) {
            // Keep the raw input (string) while editing so intermediate values like '0' or '0.' are visible.
            // Numeric coercion is performed on save (see onSave) where Number(...) is used.
            updateOrbitData(key, value as any)
        } else {
            showToastMessage('info', validation.error)
        }
    }

    const onSave = () => {
        setModals((modalState) => {
            const currentConfigData = modalState?.configured?.data as FormGroupDetailsType;
            const currentOrbitData = currentConfigData?.referenceOrbit;

            if (!validateOrbitConfigForm(currentOrbitData)) {
                return modalState;
            }

            const updatedConfigData = {
                ...currentConfigData,
                phasingAngle: Number(currentConfigData?.phasingAngle),
                referenceOrbit: {
                    ...currentOrbitData,
                    "AP": Number(currentOrbitData?.AP),
                    "RAAN": Number(currentOrbitData?.RAAN),
                    "TA": Number(currentOrbitData?.TA),
                    "altitude": Number(currentOrbitData?.altitude),
                    "eccentricity": Number(currentOrbitData?.eccentricity),
                    "inclination": Number(currentOrbitData?.inclination)
                }
            };

            setFormData((prev) => {
                if (!prev?.satellites) return prev;
                return {
                    ...prev,
                    satellites: prev.satellites.map((sat: any) =>
                        sat?.groupId === currentConfigData.groupId
                            ? { ...sat, ...updatedConfigData }
                            : sat
                    ),
                };
            });

            return {
                ...modalState,
                configured: null
            };
        });
    }

    if (!open) return null
    return (
        <Modal open={open} pinned={true} disabled={true} className={`scenario__satellite__configuration__modal`}>
            <ModalHeader className='scenario__satellite__configuration__modal__header'>
                <HeaderTitleContainer>
                    <HeaderTitle>
                        Configuration
                    </HeaderTitle>
                </HeaderTitleContainer>
                <HeaderActionsContainer>
                    {!onEdit &&
                        <HeaderAction title='Edit' onClick={() => setOnEdit(!onEdit)}>
                            <EditSvg />
                        </HeaderAction>
                    }
                    {onEdit &&
                        <HeaderAction title='Save' onClick={onSave}>
                            <SaveSvg />
                        </HeaderAction>
                    }
                    <HeaderAction title='Close' onClick={closeModal}>
                        <CloseSvg />
                    </HeaderAction>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody>
                <div className='scenario__satellite__configuration__modal__body'>
                    <div className='orbitconfig__form__label'>ORBIT DETAILS</div>
                    {(configData && configData?.satCount > 1) &&
                        <div className='orbitconfig__input__container'>
                            <span className='orbitconfig__input__label'>No. of Planes</span>
                            <div className={`orbitconfig__field__container view__only`}>
                                <input type='text' value={configData?.phasingType === "PlanePhasing" ? configData?.satCount : 1} className='orbitconfig__input' />
                            </div>
                        </div>
                    }

                    {ORBIT_FIELDS.map((orbit) => {
                        if (orbit.name === 'phasingAngle' && (configData && configData?.satCount <= 1)) return null
                        else if (orbit.name === 'phasingAngle' && (configData && configData?.satCount > 1)) {
                            return (
                                <Fragment key={orbit.name}>
                                    <div className='scenario__satellite__configuration__modal__body__devider' />
                                    <div className='orbitconfig__form__label'>PHASING CONFIGURATION</div>
                                    <div className='orbitconfig__input__container'>
                                        <span className='orbitconfig__input__label d-flex align-items-center gap-1'>{configData?.phasingType === "PlanePhasing" ? "RAAN Phasing Angle" : "TA Phasing Angle"} <span className='mb-1'>({orbit.unit})</span></span>
                                        <div className='orbit__phasing__container'>
                                            {onEdit &&
                                                <button className={`orbitconfig__phasing__button ${(autoPhasing) ? 'active' : ''}`} onClick={onAutoPhasing}>
                                                    {configData?.phasingType === 'PlanePhasing' && <RAANPhasingButton active={autoPhasing} />}
                                                    {configData?.phasingType !== 'PlanePhasing' && <TAPhasingButton active={autoPhasing} />}
                                                </button>
                                            }
                                            <div className={`orbitconfig__field__container ${!onEdit && 'view__only'}`}>
                                                <input disabled={onEdit && autoPhasing} type='text' value={String(configData?.phasingAngle)} placeholder=''
                                                    className='orbitconfig__input'
                                                    onKeyDown={onKeyDown}
                                                    onChange={(e) => {
                                                        onChangeValue(orbit.label, orbit.name, e.target.value, orbit.range[0], orbit.range[1], orbit.max_length, orbit.parameter)
                                                    }}
                                                />
                                                <div className='orbitconfig__parameter__section'><sup>o </sup></div>
                                            </div>
                                        </div>
                                    </div>
                                </Fragment>
                            )
                        }
                        return (
                            <div className='orbitconfig__input__container' key={orbit.name}>
                                <span className='orbitconfig__input__label'>{orbit.label} {orbit.unit}</span>
                                <div className={`orbitconfig__field__container ${!onEdit && 'view__only'}`}>
                                    <input type='text' value={String(orbitData?.[orbit.name])} placeholder=''
                                        className='orbitconfig__input'
                                        onKeyDown={onKeyDown}
                                        onChange={(e) => {
                                            onChangeValue(orbit.label, orbit.name, e.target.value, orbit.range[0], orbit.range[1], orbit.max_length, orbit.parameter)
                                        }}
                                    />
                                    {orbit.parameter && <div className='orbitconfig__parameter__section'>{orbit.parameter}</div>}
                                </div>
                            </div>
                        )
                    })}
                    <div className='scenario__satellite__configuration__modal__body__devider' />
                    <div className='orbitconfig__input__container pt-2'>
                        <span className='orbitconfig__input__label'>
                            Epoch
                            <CustomToolTip
                                placement='right'
                                maxWidth='350px'
                                title='Scenario start time is the default epoch, you can change it by manually editing the values. The epoch will remain same even if you change the scenario start time.'
                            >
                                <sup><EpochInfoIcon /> </sup>
                            </CustomToolTip>
                        </span>
                        {!onEdit &&
                            <div className=' d-flex align-items-center justify-content-end opacity-75'>
                                {getEpochDetails()}
                            </div>
                        }
                        {onEdit &&
                            <div className={`orbitconfig__field__container extra__large`}>
                                <DatetimeInput
                                    key={'epoch'}
                                    value={orbitData?.epoch || 0}
                                    onChange={(value) => {
                                        onChangeEpoch('epoch', value)
                                    }}
                                />
                            </div>
                        }
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default OrbitConfigModal
